import type { Locale } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import { Box, Text } from 'braid-design-system';
import { observer } from 'mobx-react';
import React, { useEffect } from 'react';

import { TRACKING_CANDIDATE_DETAILS } from 'src/constants/pageTrackingData';
import type { PageConfig } from 'src/libs/PageConfig';
import type { TrackStatusUpdateAttributes } from 'src/utils/eventsTypes';

import type { IAttachmentsStore } from '../../../stores/JobApplicationStore/AttachmentsStore';
import {
  STATE_ERROR,
  STATE_LOADED,
  STATE_LOADING,
} from '../../../stores/state';
import { trackAddAttachmentStart } from '../../../utils/events';
import ErrorAlert from '../../common/ErrorAlert';
import Loader from '../../common/Loader';

import translations from './.vocab';
import AddAttachmentItem from './AddAttachmentItem';
import AttachmentItemList from './AttachmentItemList';

interface Props {
  attachmentsStore: IAttachmentsStore;
  locale: Locale;
  isVerifiedId: TrackStatusUpdateAttributes['isVerifiedId'];
  addCustomError: PageConfig['dataDogRum']['addCustomError'];
}

const Attachments = ({
  attachmentsStore,
  locale,
  isVerifiedId,
  addCustomError,
}: Props) => {
  const { t } = useTranslations(translations);
  useEffect(() => {
    attachmentsStore.loadAttachments(addCustomError);
  });
  const addAction = async (content: File) =>
    attachmentsStore.addAttachment(content, addCustomError);

  const onAddClick = () => {
    trackAddAttachmentStart({
      ...attachmentsStore.jobApplication.baseTrackingAttributes,
      currentPage: TRACKING_CANDIDATE_DETAILS,
    });
  };
  return (
    <>
      {attachmentsStore.state === STATE_LOADED && (
        <Box marginBottom="medium">
          <AddAttachmentItem addAction={addAction} onAddClick={onAddClick} />
        </Box>
      )}
      <div>
        {attachmentsStore.state === STATE_ERROR && (
          <Box marginY="medium">
            <ErrorAlert>
              <Text>{t("We can't show your attachments right now...")}</Text>
            </ErrorAlert>
          </Box>
        )}

        {attachmentsStore.state !== STATE_ERROR && (
          <div data-testid="attachments">
            {attachmentsStore.state === STATE_LOADING && <Loader />}
            <AttachmentItemList
              attachmentsStore={attachmentsStore}
              locale={locale}
              isVerifiedId={isVerifiedId}
              addCustomError={addCustomError}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default observer(Attachments);
